import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { AnimatePresence } from "framer-motion";
import { IconCalendarPlus, IconClipboardPlus } from "@tabler/icons-react";
import { LoadingSpinner } from "@/components/LoadingSpinner/LoadingSpinner";
import { useCapabilities } from "@/providers/CapabilitiesProvider";
import { PAppointmentDetails } from "@/components/PAppointmentDetails/PAppointmentDetails";
import { PRequestDetails } from "@/components/PRequestDetails/PRequestDetails";
import { AppointmentEntry } from "./AppointmentEntry";
import { RequestEntry } from "./RequestEntry";
import { GuestSlottingCreateFlow } from "./GuestSlottingCreateFlow";
import { PanelEmptyState } from "./PanelEmptyState";
import {
  useGuestSlottingTabData,
  GUEST_SLOTTING_CHANGED_EVENT,
  type GuestSlottingChangedDetail,
} from "../hooks/useGuestSlottingTabData";
import { activeStatusDotRules, statusDotFetchFields } from "@/lib/slotting/statusDots";
import { getGuestsByIds } from "@/lib/slotting/api";
import type { Appointment, AppointmentRequest } from "@/lib/slotting/types";
import type { Guest } from "@/types/Guest";

type Props = {
  guestId: string;
  firstName?: string;
  lastName?: string;
};

/**
 * Merged slotting tab of the guest-detail popup: open requests (the ones
 * still needing action) above the booked appointments, plus create actions
 * for both — replaces the former separate appointments/requests tabs.
 */
export const AppointmentsAndRequestsTab: React.FC<Props> = ({
  guestId,
  firstName,
  lastName,
}) => {
  const t = useTranslations("guestDetails");
  const t3 = useTranslations("videoMessages");
  const tErrors = useTranslations("errors");
  const { can } = useCapabilities();
  const canWrite = can("slotting:write");

  const {
    eventId,
    appointments,
    openRequests,
    loading,
    appointmentsError,
    requestsError,
    offeringMap,
    offeringsById,
    treatmentMap,
    laneMap,
    guestNameMap,
  } = useGuestSlottingTabData(guestId, firstName, lastName);

  const [previewAppointment, setPreviewAppointment] = useState<Appointment | null>(null);

  // Status dots for the appointment preview: lazily fetch the appointment's
  // guests (projection widened to the rules' fields) when the preview opens on
  // an offering that has rules. No rules ⇒ no fetch, no dots.
  const previewDotRules = useMemo(
    () =>
      previewAppointment
        ? activeStatusDotRules(
            offeringsById.get(previewAppointment.offeringId)?.statusDotRules,
          )
        : [],
    [previewAppointment, offeringsById],
  );
  const [dotGuestRecords, setDotGuestRecords] = useState<Map<string, Guest>>(new Map());
  useEffect(() => {
    if (!previewAppointment || previewDotRules.length === 0) {
      setDotGuestRecords((prev) => (prev.size === 0 ? prev : new Map()));
      return;
    }
    let cancelled = false;
    const ids = previewAppointment.guests.map((g) => g.guestId);
    getGuestsByIds(eventId, ids, statusDotFetchFields(previewDotRules)).then(
      (guests) => {
        if (cancelled) return;
        setDotGuestRecords(
          new Map(
            guests.flatMap((g) => (g.guestId ? [[g.guestId, g] as const] : [])),
          ),
        );
      },
    );
    return () => {
      cancelled = true;
    };
  }, [previewAppointment, previewDotRules, eventId]);
  const [previewRequest, setPreviewRequest] = useState<AppointmentRequest | null>(null);
  const [creating, setCreating] = useState<"appointment" | "request" | null>(null);

  const hasAppointments = !!appointments && appointments.length > 0;
  const hasOpenRequests = openRequests.length > 0;
  // A failed read is not an empty history: with one side failed and the other
  // empty, the big "no appointments" used to win and hid the error, so the
  // section's own inline error below could never be reached.
  const empty =
    !hasAppointments && !hasOpenRequests && !appointmentsError && !requestsError;

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {loading ? (
        <LoadingSpinner />
      ) : appointmentsError && requestsError ? (
        <div className="p-4 text-center font-error">{appointmentsError}</div>
      ) : (
        <div className="flex flex-1 flex-col mt-3">
          {/* Create actions — kept at the top so "new request" stays reachable
              while the requests section is hidden at 0 open requests. */}
          {canWrite && (
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setCreating("appointment")}
                className="flex-1 min-w-0 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg border outline-tertiary body-4-medium font-secondary hover:surface-secondary hover:font-primary transition-colors"
              >
                <IconCalendarPlus size={15} className="shrink-0" />
                <span className="truncate">{t("newAppointmentAction")}</span>
              </button>
              <button
                onClick={() => setCreating("request")}
                className="flex-1 min-w-0 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg border outline-tertiary body-4-medium font-secondary hover:surface-secondary hover:font-primary transition-colors"
              >
                <IconClipboardPlus size={15} className="shrink-0" />
                <span className="truncate">{t("newRequestAction")}</span>
              </button>
            </div>
          )}

          {empty ? (
            <PanelEmptyState riv="NoSlot" title={t3("noAppointments")} />
          ) : (
            <>
              {/* A failed requests read gets its section back: hidden at zero,
                  it was the one failure this tab never mentioned. */}
              {(hasOpenRequests || requestsError) && (
                <section className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="body-4-medium font-tertiary uppercase tracking-wide">
                      {t("openRequestsSection")}
                    </h3>
                    {hasOpenRequests && (
                      <span className="chip-blue-bg chip-blue-fg rounded-full px-1.5 py-px text-[10px] font-medium tabular-nums">
                        {openRequests.length}
                      </span>
                    )}
                  </div>
                  {requestsError && (
                    <p className="body-4-regular font-error mb-2" role="alert">
                      {tErrors("somethingWentWrong")}
                    </p>
                  )}
                  <ul className="flex flex-col space-y-3">
                    {openRequests.map((request) => (
                      <RequestEntry
                        request={request}
                        treatmentMap={treatmentMap}
                        offeringName={offeringMap.get(request.offeringId)}
                        key={request.appointmentRequestId}
                        onPreview={setPreviewRequest}
                      />
                    ))}
                  </ul>
                </section>
              )}

              <section>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="body-4-medium font-tertiary uppercase tracking-wide">
                    {t("bookedAppointmentsSection")}
                  </h3>
                </div>
                {hasAppointments ? (
                  <ul className="flex flex-col space-y-3">
                    {appointments!.map((appointment) => (
                      <AppointmentEntry
                        appointment={appointment}
                        key={appointment.appointmentId}
                        firstName={firstName}
                        lastName={lastName}
                        onPreview={setPreviewAppointment}
                      />
                    ))}
                  </ul>
                ) : (
                  <p className={`body-4-regular ${appointmentsError ? "font-error" : "font-tertiary"}`}>
                    {appointmentsError ?? t3("noAppointments")}
                  </p>
                )}
              </section>
            </>
          )}
        </div>
      )}

      <AnimatePresence>
        {previewAppointment && (
          <PAppointmentDetails
            appointment={previewAppointment}
            eventId={eventId}
            offeringName={offeringMap.get(previewAppointment.offeringId)}
            treatmentName={
              previewAppointment.treatmentId
                ? treatmentMap.get(previewAppointment.treatmentId)
                : undefined
            }
            laneNames={previewAppointment.laneIds
              .map((id) => laneMap.get(id))
              .filter((n): n is string => Boolean(n))}
            guestNames={guestNameMap}
            statusDotRules={previewDotRules.length > 0 ? previewDotRules : undefined}
            guestRecords={previewDotRules.length > 0 ? dotGuestRecords : undefined}
            onClose={() => setPreviewAppointment(null)}
          />
        )}
        {previewRequest && (
          <PRequestDetails
            request={previewRequest}
            eventId={eventId}
            offeringName={offeringMap.get(previewRequest.offeringId)}
            treatmentMap={treatmentMap}
            onClose={() => setPreviewRequest(null)}
          />
        )}
        {creating && (
          <GuestSlottingCreateFlow
            eventId={eventId}
            kind={creating}
            guest={{ guestId, firstName, lastName }}
            onClose={() => setCreating(null)}
            onCreated={(kind) => {
              setCreating(null);
              // Broadcast instead of refetching directly: stacked guest-detail
              // popups mount their own provider instances, so the popup the
              // user is looking at is not necessarily the one that owns this
              // create flow. Every mounted tab for this guest refetches
              // (including this one, via the hook's listener).
              window.dispatchEvent(
                new CustomEvent<GuestSlottingChangedDetail>(
                  GUEST_SLOTTING_CHANGED_EVENT,
                  { detail: { guestId, kind } },
                ),
              );
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
